import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import audioBuzzAPI from '../../services/audioBuzzApi';

const AudioFileUpload = ({ onAnalysisComplete, onError }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisOptions, setAnalysisOptions] = useState({
    thresholdStrong: -45.0,
    stdThreshold1: 15.0,
    stdThreshold2: 14.0,
    outlierCountThreshold: 7,
    baseFreq: ''
  });

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setIsAnalyzing(true);

    try {
      let result;
      const options = {
        ...analysisOptions,
        baseFreq: analysisOptions.baseFreq ? parseInt(analysisOptions.baseFreq) : undefined
      };

      if (file.name.toLowerCase().endsWith('.wav')) {
        result = await audioBuzzAPI.analyzeWavFile(file, options);
      } else if (file.name.toLowerCase().endsWith('.txt')) {
        result = await audioBuzzAPI.analyzeTxtFile(file, options);
      } else {
        throw new Error('Unsupported file type. Please upload WAV or TXT files.');
      }

      onAnalysisComplete(result);
    } catch (error) {
      onError(error.message);
    } finally {
      setIsAnalyzing(false);
    }
  }, [analysisOptions, onAnalysisComplete, onError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/wav': ['.wav'],
      'text/plain': ['.txt']
    },
    multiple: false
  });

  const handleOptionChange = (key, value) => {
    setAnalysisOptions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
        <h3 className="text-lg font-semibold text-white mb-4">Analysis Parameters</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Base Frequency (Hz)</label>
            <input
              type="number"
              value={analysisOptions.baseFreq}
              onChange={(e) => handleOptionChange('baseFreq', e.target.value)}
              placeholder="Auto-detect from filename"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Strong Threshold (dB)</label>
            <input
              type="number"
              step="0.1"
              value={analysisOptions.thresholdStrong}
              onChange={(e) => handleOptionChange('thresholdStrong', parseFloat(e.target.value))}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Std Threshold 1</label>
            <input
              type="number"
              step="0.1"
              value={analysisOptions.stdThreshold1}
              onChange={(e) => handleOptionChange('stdThreshold1', parseFloat(e.target.value))}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Std Threshold 2</label>
            <input
              type="number"
              step="0.1"
              value={analysisOptions.stdThreshold2}
              onChange={(e) => handleOptionChange('stdThreshold2', parseFloat(e.target.value))}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Outlier Count Threshold</label>
            <input
              type="number"
              value={analysisOptions.outlierCountThreshold}
              onChange={(e) => handleOptionChange('outlierCountThreshold', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      <div
        {...getRootProps()}
        className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200 ${
          isDragActive 
            ? 'border-blue-500 bg-gray-800' 
            : 'border-gray-700 hover:border-gray-600 hover:bg-gray-900'
        } ${isAnalyzing ? 'pointer-events-none opacity-75' : ''}`}
      >
        <input {...getInputProps()} />
        {isAnalyzing ? (
          <div className="space-y-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="text-gray-300 font-medium">Analyzing audio file...</p>
          </div>
        ) : isDragActive ? (
          <div className="space-y-2">
            <div className="text-4xl">📂</div>
            <p className="text-blue-400 font-medium">Drop the file here...</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-5xl">📁</div>
            <div className="space-y-2">
              <p className="text-lg font-medium text-white">
                Drag & drop a WAV or TXT file here, or click to select
              </p>
              <p className="text-sm text-gray-400">
                Supported formats: .wav, .txt
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioFileUpload;
